import { type ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { careersPath } from '@/modules/careers/careers';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { FINLAY_MARKETING_URL } from '@/modules/public-shortlist/constants';
import { getInitials } from '@/utils/getInitials';

// The portal's look (Inter, its 14px scale and tokens) is the `.portal` block in index.css; PortalLayout sets it the
// same way. On <body>, so portalled surfaces (dialogs, popovers) share it.
const PORTAL_THEME_CLASS = 'portal';

interface CareersLayoutProps {
  title: string;
  description?: string;
  /** The company whose page this is; until it loads, the header shows Finlay's logo. */
  company?: { name: string; slug: string };
  /** Rise the content in (its `enterStep` children), when it replaces the loading skeleton. */
  animateEnter?: boolean;
  children: ReactNode;
}

/**
 * The Careers Page frame. Applicants are applying to the company, so the
 * header carries its name and Finlay steps back to a footer credit.
 */
export function CareersLayout({
  title,
  description,
  company,
  animateEnter = false,
  children,
}: CareersLayoutProps) {
  useEffect(() => {
    document.body.classList.add(PORTAL_THEME_CLASS);
    return () => document.body.classList.remove(PORTAL_THEME_CLASS);
  }, []);

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <DocumentMetadata description={description} title={title} />
      <header className="bg-background/95 sticky top-0 z-10 border-b backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between px-5 md:px-10">
          {company ? (
            <Link
              className="focus-visible:ring-ring/50 flex items-center gap-2.5 rounded-md outline-none focus-visible:ring-[3px]"
              to={careersPath(company.slug)}
            >
              <span
                aria-hidden
                className="bg-foreground grid size-7 place-items-center rounded-[7px] text-[11px] font-bold tracking-wide text-white"
              >
                {getInitials(company.name)}
              </span>
              <span className="text-[15px] font-semibold tracking-[-0.01em]">
                {company.name}
              </span>
            </Link>
          ) : (
            <img
              alt="Finlay"
              className="h-5 w-auto object-contain"
              src="/logo.png"
            />
          )}
          <span className="text-muted-foreground text-[13px] font-medium">
            Careers
          </span>
        </div>
      </header>
      <main
        className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col px-5 pt-8 pb-20 md:px-10 md:pt-10"
        data-enter={animateEnter || undefined}
      >
        {children}
      </main>
      <footer className="mx-auto flex w-full max-w-[1280px] justify-end px-5 pb-10 md:px-10">
        <a
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs transition-colors"
          href={FINLAY_MARKETING_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          Hiring powered by
          <img alt="Finlay" className="h-4 w-auto" src="/logo.png" />
        </a>
      </footer>
    </div>
  );
}
